'use client';

import type { CSSProperties, ReactNode } from 'react';
import { useTranslations } from 'next-intl';
import CustomLink from 'components/shared/CustomLink';
import { useMarketingBannerOffset } from 'components/shared/Header/MarketingBanner';
import { getCompressedAssetUrl } from 'lib/config/assetUrls';
import { BackgroundVideo } from './BackgroundVideo';
import { homeAssetSrc } from './v3/homeAssetSrc';
import v3 from './v3/v3.module.css';
import styles from './RingProHero.module.css';

const DESKTOP_LAYOUT_MIN = 992;

const HERO_ASSET_DIR = '/web_v2/home-v4/hero';
const HERO_POSTER_DIR = '/web_v2/home-v4/video-posters';

const cdn = (path: string) => getCompressedAssetUrl(homeAssetSrc(path));

/**
 * Art direction for the hero, one row per variant, widest first.
 *
 * The <source media> list for the VIDEO and the one for the POSTER are both
 * derived from this array, so a variant can never have a poster that belongs to
 * a different file. That drift is what made the swap visible: the poster set had
 * two entries against the video's three, so at >= 16/9 the poster was the
 * landscape still while `max-wide.mp4` played, and neither poster was a frame of
 * any video. Each poster here is the exact first frame of its own file, decoded
 * from the SpeedSize-served encode the browser actually receives (`min.mp4` is
 * re-encoded in delivery, so the origin file's frame is not the same picture).
 *
 * `.poster img` and `.backgroundLayer video` share every box property in
 * RingProHero.module.css — position, size, object-fit and object-position, in
 * the base rule AND in the stacked media override — so the two are painted into
 * an identical box and the handover is a straight swap.
 *
 * Caveat, and it is a real one: `media` on `<source>` is evaluated ONCE during
 * resource selection and is not re-run on resize or rotation, so a rotated phone
 * keeps min.mp4. Both compositions are `object-fit: cover`, so that is a
 * crop-quality difference on a gesture, traded for the cold-load win.
 */
const HERO_VARIANTS = [
  {
    media: `(min-width: ${DESKTOP_LAYOUT_MIN}px) and (min-aspect-ratio: 16/9)`,
    video: 'max-wide.mp4',
    poster: 'max-wide/first-frame.jpg',
  },
  {
    media: `(min-width: ${DESKTOP_LAYOUT_MIN}px) and (min-aspect-ratio: 6/5)`,
    video: 'max.mp4',
    poster: 'max/first-frame.jpg',
  },
  // Unconditional fallback — must stay last, and must carry no `media`.
  { media: undefined, video: 'min.mp4', poster: 'min/first-frame.jpg' },
] as const;

const HERO_VIDEO_SOURCES = HERO_VARIANTS.map(({ video, media }) => ({
  src: cdn(`${HERO_ASSET_DIR}/${video}`),
  media,
}));

/** Same order as the video: every entry but the last is a <source>. */
const HERO_POSTER_SOURCES = HERO_VARIANTS.slice(0, -1).map(
  ({ poster, media }) => ({ src: cdn(`${HERO_POSTER_DIR}/${poster}`), media }),
);
const HERO_POSTER_FALLBACK = cdn(
  `${HERO_POSTER_DIR}/${HERO_VARIANTS[HERO_VARIANTS.length - 1].poster}`,
);

/**
 * The hero's art-directed poster + video layer. Exported so the US Ring PRO
 * revamp hero (`components/ring-pro-us-home/hero`) paints the exact same
 * media, LCP behaviour and stacked crop as this one.
 */
export const RingProHeroBackground = ({
  posterSrc,
}: {
  /** Replaces the shared art-directed posters. The video files stay. */
  posterSrc?: string;
} = {}) => (
  <div className={styles.backgroundLayer} aria-hidden>
    <picture className={styles.poster}>
      {posterSrc
        ? null
        : HERO_POSTER_SOURCES.map((p) => (
            <source key={p.media} media={p.media} srcSet={p.src} />
          ))}
      <img
        src={posterSrc ?? HERO_POSTER_FALLBACK}
        alt=""
        loading="eager"
        fetchPriority="high"
        decoding="async"
      />
    </picture>
    <BackgroundVideo
      sources={HERO_VIDEO_SOURCES}
      loop={false}
      playOnce
      preload="auto"
      visibilityThreshold={0.05}
    />
  </div>
);

type RingProHeroProps = {
  note?: ReactNode;
  primaryButtonType: string;
  'aria-label'?: string;
  /** True when a campaign hero banner renders above this on the homepage —
      drops this section's own header-clearance top margin (the banner owns it). */
  hasCampaignAbove?: boolean;
};

export const RingProHero = ({
  note,
  primaryButtonType,
  'aria-label': ariaLabel,
  hasCampaignAbove,
}: RingProHeroProps) => {
  const t = useTranslations('homepage');
  const bannerPx = useMarketingBannerOffset();

  return (
    <section
      aria-label={ariaLabel}
      data-hero="ring-pro"
      data-header-theme="light"
      className={`${styles.section} ${
        hasCampaignAbove ? styles.campaignAbove : ''
      }`}
      style={
        {
          '--hero-banner-h': `${bannerPx}px`,
          // Campaign-above is not under the header, so the copy keeps the
          // previous 32px inset (no AE copy drift).
          '--hero-copy-banner-h': `${hasCampaignAbove ? 32 : bannerPx}px`,
        } as CSSProperties
      }
    >
      <RingProHeroBackground />
      <div className={styles.bottomFade} aria-hidden />
      <div className={styles.content}>
        <div className={styles.copy}>
          <div className={styles.titleGroup}>
            {/* Eyebrow is a <p>: the page's only h1 is the SrOnly one in
                home-client, and section headings are h2 (INV-13/15). */}
            <p className={styles.eyebrow}>{t('homepage.ringPro.title')}</p>
            <h2 className={styles.title}>{t('homepage.ringPro.headline')}</h2>
            <p className={styles.body}>{t('homepage.ringPro.body')}</p>
          </div>
          <div className={styles.ctaRow}>
            <CustomLink
              href="/ring-pro/buy"
              className={`${v3.pill} ${v3.pillPrimary} ${styles.cta}`}
              data-buttontype={primaryButtonType}
            >
              {t('homepage.cta.buyNow')}
            </CustomLink>
            <CustomLink
              href="/ring-pro"
              className={`${v3.pill} ${v3.pillSecondary} ${styles.cta} ${styles.ctaGlass}`}
              aria-label={t('homepage.ringPro.aria.explore')}
              data-buttontype="ring_pro_banner_learn_button"
            >
              {t('homepage.cta.explore')}
            </CustomLink>
          </div>
        </div>
        {note ? <div className={styles.note}>{note}</div> : null}
      </div>
    </section>
  );
};

export default RingProHero;
